import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Loader2, Save, Settings, AlertTriangle, Info, Zap, Landmark, Ban, Plus, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtEur = (v) => Number(v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });

export default function SepaLimitsModal({ open, onClose, onSaved, currentRole }) {
  const isAdmin = currentRole === 'admin';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [instantLimit, setInstantLimit] = useState('');
  const [instantMargin, setInstantMargin] = useState('');
  const [ordinaryLimit, setOrdinaryLimit] = useState('');
  const [ordinaryMargin, setOrdinaryMargin] = useState('');
  const [excludedNames, setExcludedNames] = useState([]);
  const [newName, setNewName] = useState('');
  const [meta, setMeta] = useState({});
  const token = localStorage.getItem('hw_token');

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API_URL}/api/admin/sepa-split/config`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) {
          const d = await r.json();
          setInstantLimit(String(d.instant_limit_eur));
          setInstantMargin(String(d.instant_margin_eur));
          setOrdinaryLimit(String(d.ordinary_limit_eur));
          setOrdinaryMargin(String(d.ordinary_margin_eur));
          setExcludedNames(d.instant_excluded_names || []);
          setMeta(d);
        }
      } catch (_) { /* ignore */ }
      setLoading(false);
    })();
  }, [open, token]);

  const addName = () => {
    const n = newName.trim().toUpperCase().replace(/\s+/g, ' ');
    if (!n) return;
    if (excludedNames.includes(n)) { toast.info('Nome già presente'); return; }
    setExcludedNames([...excludedNames, n]);
    setNewName('');
  };

  const removeName = (n) => setExcludedNames(excludedNames.filter((x) => x !== n));

  const handleSave = async () => {
    const il = parseFloat(instantLimit);
    const im = parseFloat(instantMargin);
    const ol = parseFloat(ordinaryLimit);
    const om = parseFloat(ordinaryMargin);
    if ([il, ol].some((v) => isNaN(v) || v <= 0)) { toast.error('Limiti invalidi (devono essere > 0)'); return; }
    if ([im, om].some((v) => isNaN(v) || v < 0)) { toast.error('Margini invalidi'); return; }
    if (im >= il || om >= ol) { toast.error('Il margine deve essere inferiore al limite'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/sepa-split/config`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          instant_limit_eur: il,
          instant_margin_eur: im,
          ordinary_limit_eur: ol,
          ordinary_margin_eur: om,
          instant_excluded_names: excludedNames,
        }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || `Errore HTTP ${r.status}`);
      }
      const d = await r.json();
      toast.success(`Limiti aggiornati: istantanei ${fmtEur(d.instant_effective_eur)} — ordinari ${fmtEur(d.ordinary_effective_eur)}`);
      onSaved && onSaved(d);
      onClose();
    } catch (e) {
      toast.error(e.message);
    }
    setSaving(false);
  };

  const effInstant = (parseFloat(instantLimit) || 0) - (parseFloat(instantMargin) || 0);
  const effOrdinary = (parseFloat(ordinaryLimit) || 0) - (parseFloat(ordinaryMargin) || 0);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sepa-limits-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-slate-600" />
            Limiti SEPA Banca di Asti
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center">
            <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
          </div>
        ) : (
          <div className="space-y-4">
            <Card className="p-3 bg-blue-50 border-blue-200">
              <div className="flex items-start gap-2 text-xs text-blue-900">
                <Info className="h-4 w-4 mt-0.5 shrink-0" />
                <p>
                  La banca applica <strong>massimali giornalieri separati</strong>: uno per i bonifici{' '}
                  <strong>istantanei</strong> e uno per gli <strong>ordinari</strong>. Il margine di
                  sicurezza viene sottratto al limite per evitare rifiuti su arrotondamenti.
                </p>
              </div>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Istantanei */}
              <Card className="p-4 space-y-3 border-blue-200">
                <div className="flex items-center gap-2 font-semibold text-blue-800 text-sm">
                  <Zap className="h-4 w-4" /> Bonifici istantanei
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Limite giornaliero (€)</label>
                  <Input type="number" step="500" min="0" value={instantLimit}
                    onChange={(e) => setInstantLimit(e.target.value)} disabled={!isAdmin}
                    data-testid="instant-limit-config-input" placeholder="20000" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Margine sicurezza (€)</label>
                  <Input type="number" step="100" min="0" value={instantMargin}
                    onChange={(e) => setInstantMargin(e.target.value)} disabled={!isAdmin}
                    data-testid="instant-margin-config-input" placeholder="500" />
                </div>
                <div className="flex items-center justify-between bg-blue-50 rounded px-2 py-1.5">
                  <span className="text-xs text-blue-800">Soglia effettiva:</span>
                  <span className="font-bold text-blue-700 text-sm" data-testid="instant-effective-display">{fmtEur(effInstant)}</span>
                </div>
              </Card>

              {/* Ordinari */}
              <Card className="p-4 space-y-3 border-slate-300">
                <div className="flex items-center gap-2 font-semibold text-slate-700 text-sm">
                  <Landmark className="h-4 w-4" /> Bonifici ordinari
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Limite giornaliero (€)</label>
                  <Input type="number" step="500" min="0" value={ordinaryLimit}
                    onChange={(e) => setOrdinaryLimit(e.target.value)} disabled={!isAdmin}
                    data-testid="ordinary-limit-config-input" placeholder="50000" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Margine sicurezza (€)</label>
                  <Input type="number" step="100" min="0" value={ordinaryMargin}
                    onChange={(e) => setOrdinaryMargin(e.target.value)} disabled={!isAdmin}
                    data-testid="ordinary-margin-config-input" placeholder="500" />
                </div>
                <div className="flex items-center justify-between bg-slate-100 rounded px-2 py-1.5">
                  <span className="text-xs text-slate-700">Soglia effettiva:</span>
                  <span className="font-bold text-slate-800 text-sm" data-testid="ordinary-effective-display">{fmtEur(effOrdinary)}</span>
                </div>
              </Card>
            </div>

            {/* Esclusi dagli istantanei */}
            <Card className="p-4 space-y-3 border-violet-200">
              <div className="flex items-center gap-2 font-semibold text-violet-800 text-sm">
                <Ban className="h-4 w-4" /> Beneficiari esclusi dai bonifici istantanei
              </div>
              <p className="text-xs text-slate-500">
                Le banche di questi clienti non accettano bonifici istantanei: verranno sempre inseriti
                nei file <strong>ordinari</strong>. Il confronto ignora maiuscole e ordine delle parole.
              </p>
              <div className="flex flex-wrap gap-2" data-testid="excluded-names-list">
                {excludedNames.length === 0 && (
                  <span className="text-xs text-slate-400 italic">Nessun beneficiario escluso</span>
                )}
                {excludedNames.map((n) => (
                  <Badge key={n} className="bg-violet-100 text-violet-800 gap-1 pr-1" data-testid={`excluded-name-${n.replace(/\s+/g, '-')}`}>
                    {n}
                    {isAdmin && (
                      <button onClick={() => removeName(n)} className="ml-1 hover:text-red-600" data-testid={`remove-excluded-${n.replace(/\s+/g, '-')}`}>
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </Badge>
                ))}
              </div>
              {isAdmin && (
                <div className="flex gap-2">
                  <Input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addName()}
                    placeholder="es. MARCELLO BORRELLO"
                    className="h-8 text-sm"
                    data-testid="new-excluded-name-input"
                  />
                  <Button size="sm" variant="outline" onClick={addName} data-testid="add-excluded-name-btn">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Aggiungi
                  </Button>
                </div>
              )}
            </Card>

            {!isAdmin && (
              <Card className="p-3 bg-amber-50 border-amber-200">
                <div className="flex items-center gap-2 text-xs text-amber-900">
                  <AlertTriangle className="h-4 w-4" />
                  <span>Solo gli amministratori possono modificare i limiti. Visualizzazione in sola lettura.</span>
                </div>
              </Card>
            )}

            {meta.source === 'db' && meta.updated_by && (
              <p className="text-[10px] text-slate-400 text-right">
                Ultima modifica: {meta.updated_by} il {new Date(meta.updated_at).toLocaleString('it-IT')}
              </p>
            )}
            {meta.source === 'env' && (
              <p className="text-[10px] text-slate-400 text-right">
                Valori di default da configurazione di sistema
              </p>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose}>Chiudi</Button>
              {isAdmin && (
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="save-sepa-limits-btn"
                >
                  {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Salvataggio...</> : <><Save className="h-4 w-4 mr-2" /> Salva limiti</>}
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
